import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api";

import { Input } from "./ui/shadcn/input";
import {
  FiX,
  FiZoomIn,
  FiZoomOut,
  FiCheck,
  FiFolder,
  FiLock,
} from "react-icons/fi";
import AssetThumbnail from "./AssetThumbnail";
import { useShow } from "@/contexts/ShowContext";
import Loader from "./loader";
import { FaAngleLeft, FaAngleRight } from "react-icons/fa";
import Button from "./ui/Button";
import { Checkbox } from "./ui/shadcn/checkbox";

export default function AssetsBrowser({
  onSelect,
  header = "Find Assets",
  tags = [],
  showUntagged = false,
  mediaType = "",
  multiSelect = false,
  onClose,
}) {
  const [search, setSearch] = useState("");
  const [filterTypes, setFilterTypes] = useState({
    video: { value: true, label: "Videos" },
    image: { value: true, label: "Images" },
    audio: { value: true, label: "Audio" },
  });
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [assets, setAssets] = useState([]);
  const [folders, setFolders] = useState([]);
  const [ancestors, setAncestors] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAssets, setTotalAssets] = useState(0);
  const [loading, setLoading] = useState(true);
  const [gridCols, setGridCols] = useState(2);
  const { show } = useShow();

  const isSearching = !!search;

  const fetchFolders = async () => {
    try {
      const url = `/api/shows/${show._id}/folders?folder=${currentFolderId || ""}`;
      const res = await apiFetch(url);
      if (!res.ok) throw new Error("Failed to fetch folders");
      const data = await res.json();
      setFolders(data.folders || []);
      setAncestors(data.ancestors || []);
    } catch (error) {
      console.error("Error fetching folders:", error);
    }
  };

  const fetchAssets = async (pageIndex) => {
    try {
      const activeMediaType =
        mediaType ||
        Object.entries(filterTypes)
          .filter(([, v]) => v.value)
          .map(([k]) => k)
          .join(",");
      const params = new URLSearchParams();
      params.set("page", String(pageIndex || page));
      params.set("search", search);
      params.set("tags", tags.map((tag) => tag).join(","));
      params.set("showUntagged", String(showUntagged));
      params.set("mediaType", activeMediaType);
      if (!isSearching) {
        params.set("folder", currentFolderId || "");
      }
      const assetsResponse = await apiFetch(
        `/api/shows/${show._id}/assets?${params.toString()}`,
      );
      if (!assetsResponse.ok) throw new Error("Failed to fetch assets");
      const assetsData = await assetsResponse.json();

      setPage(assetsData.page);
      setTotalPages(assetsData.totalPages);
      setTotalAssets(assetsData.totalAssets);
      setPageSize(assetsData.pageSize);
      setAssets(assetsData.assets);
    } catch (error) {
      console.error("Error fetching assets:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    const getData = async () => {
      setPage(1);
      setLoading(true);
      await Promise.all([fetchFolders(), fetchAssets(1)]);
      setLoading(false);
    };
    const debounced = setTimeout(() => {
      getData();
    }, 500);
    return () => clearTimeout(debounced);
  }, [search, filterTypes, currentFolderId]);

  const handleNavigateToFolder = (folderId) => {
    setCurrentFolderId(folderId);
  };

  const handlePreviousPage = async () => {
    if (page > 1) {
      setPage(page - 1);
      setLoading(true);
      await fetchAssets(page - 1);
      setLoading(false);
    }
  };

  const handleNextPage = async () => {
    if (page < totalPages) {
      setPage(page + 1);
      setLoading(true);
      await fetchAssets(page + 1);
      setLoading(false);
    }
  };

  const handleSelectAsset = (asset) => {
    if (multiSelect) {
      setSelectedAssets((prev) =>
        prev.some((a) => a._id === asset._id)
          ? prev.filter((a) => a._id !== asset._id)
          : [...prev, asset],
      );
    } else {
      if (onSelect) onSelect(asset);
    }
  };

  const handleConfirmSelection = () => {
    if (onSelect) onSelect(selectedAssets);
  };

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !loading) {
        if (multiSelect && selectedAssets.length > 0) {
          handleConfirmSelection();
        } else if (!multiSelect && assets.length) {
          handleSelectAsset(assets[0]);
        }
      }
    },
    [loading, multiSelect, selectedAssets, assets],
  );

  useEffect(() => {
    const onKeyDown = (e) => handleKeyDown(e);
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleKeyDown]);

  return (
    <div className="w-full h-full flex flex-col gap-4 relative overflow-hidden">
      {header && (
        <div className="px-4 pt-4 flex justify-between items-center">
          <h1 className="text-xl font-bold border-l-4 border-primary pl-4 capitalize">
            {header}
          </h1>
          <button
            type="button"
            onClick={() => onClose(null)}
            className="shrink-0 -mr-1 -mt-1 p-1.5 rounded-md text-secondary-foreground/70 hover:text-foreground hover:bg-secondary/60 cursor-pointer"
            title="Close">
            <FiX className="w-5 h-5" />
          </button>
        </div>
      )}
      <div className="flex w-full relative px-4">
        <Input
          type="text"
          placeholder="Search"
          className="w-full"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />
        {search && (
          <FiX
            className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-secondary-foreground/50 hover:text-secondary-foreground cursor-pointer"
            onClick={() => setSearch("")}
          />
        )}
      </div>
      {!mediaType && (
        <div className="flex items-center gap-3 px-4">
          {Object.keys(filterTypes).map((key) => (
            <div className="flex items-center gap-2" key={key}>
              <Checkbox
                name={`filter-${key}`}
                checked={filterTypes[key]?.value}
                onCheckedChange={(checked) =>
                  setFilterTypes((prev) => ({
                    ...prev,
                    [key]: { ...prev[key], value: checked },
                  }))
                }
              />
              <label
                className="text-[10px] text-secondary-foreground/80 hover:text-secondary-foreground capitalize select-none cursor-pointer"
                onClick={() =>
                  setFilterTypes((prev) =>
                    Object.fromEntries(
                      Object.entries(prev).map(([k, v]) => [
                        k,
                        { ...v, value: k === key },
                      ]),
                    ),
                  )
                }>
                {filterTypes[key]?.label}
              </label>
            </div>
          ))}
        </div>
      )}

      {!isSearching ? (
        <div className="flex items-center gap-2 px-4 text-xs text-secondary-foreground">
          <span
            className={`cursor-pointer hover:text-white ${!currentFolderId ? "text-white" : ""}`}
            onClick={() => handleNavigateToFolder(null)}>
            Root
          </span>
          {ancestors.map((a, i) => (
            <span
              key={a._id}
              className="flex items-center gap-1 truncate max-w-[160px]">
              <span>/</span>
              <span
                className={`cursor-pointer hover:text-white truncate ${i === ancestors.length - 1 ? "text-white" : ""}`}
                onClick={() => handleNavigateToFolder(a._id)}>
                {a.name}
              </span>
            </span>
          ))}
        </div>
      ) : (
        <div className="px-4 text-xs text-secondary-foreground/70">
          Search results across all visible folders
        </div>
      )}

      <div className="flex-1 overflow-y-auto relative px-4 py-1">
        <div
          className="grid gap-4"
          style={{
            gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
          }}>
          {!isSearching &&
            folders.map((folder) => (
              <div
                key={folder._id}
                onClick={() => handleNavigateToFolder(folder._id)}
                onDoubleClick={() => handleNavigateToFolder(folder._id)}
                className="flex flex-col gap-2 cursor-pointer">
                <div className="relative w-full aspect-video bg-card rounded-md overflow-hidden flex items-center justify-center hover:bg-secondary">
                  <FiFolder className="w-10 h-10 text-primary" />
                  {folder.visibility === "private" && (
                    <div className="absolute top-1 left-1 flex items-center gap-1 text-[10px] bg-background/80 text-secondary-foreground rounded-sm px-1 py-0.5">
                      <FiLock className="w-3 h-3" /> Private
                    </div>
                  )}
                </div>
                <span className="text-sm text-secondary-foreground truncate">
                  {folder.name}
                </span>
              </div>
            ))}
          {assets.map((asset) => {
            const isSelected =
              multiSelect && selectedAssets.some((a) => a._id === asset._id);
            return (
              <div
                key={asset._id}
                onClick={() => handleSelectAsset(asset)}
                className="flex flex-col gap-2 cursor-pointer">
                <div
                  className={`relative w-full aspect-video bg-card rounded-md overflow-hidden ${isSelected ? "ring-2 ring-primary" : ""}`}>
                  <AssetThumbnail asset={asset} />
                  {isSelected && (
                    <div className="absolute top-1 right-1 bg-primary text-primary-foreground rounded-full p-0.5">
                      <FiCheck className="w-3 h-3" />
                    </div>
                  )}
                </div>
                <span className="text-sm text-secondary-foreground truncate">
                  {asset.name}
                </span>
              </div>
            );
          })}
        </div>
        {folders.length === 0 && assets.length === 0 && (
          <div className="flex flex-1 items-center justify-center h-full">
            <span className="text-secondary-foreground/80 text-sm">
              No assets found
            </span>
          </div>
        )}
        {loading && <Loader />}
      </div>
      <div className="flex items-center gap-2 px-4 py-2">
        <Button
          type="secondary"
          icon={<FaAngleLeft className="w-4 h-4" />}
          onClick={handlePreviousPage}
          disabled={page === 1}
        />
        <span>
          {page}/{totalPages}
        </span>
        <Button
          type="secondary"
          icon={<FaAngleRight className="w-4 h-4" />}
          onClick={handleNextPage}
          disabled={page === totalPages}
        />
        <span>{`${(page - 1) * pageSize + 1}-${(page - 1) * pageSize + pageSize > totalAssets ? totalAssets : (page - 1) * pageSize + pageSize} of ${totalAssets}`}</span>
        <div className="flex items-center gap-1 ml-auto">
          <Button
            type="secondary"
            icon={<FiZoomIn className="w-4 h-4" />}
            onClick={() => setGridCols((prev) => Math.max(1, prev - 1))}
            disabled={gridCols === 1}
          />
          <Button
            type="secondary"
            icon={<FiZoomOut className="w-4 h-4" />}
            onClick={() => setGridCols((prev) => Math.min(5, prev + 1))}
            disabled={gridCols === 5}
          />
          {multiSelect && (
            <Button
              type="primary"
              onClick={handleConfirmSelection}
              disabled={selectedAssets.length === 0}>
              Select ({selectedAssets.length})
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
